import { ref, computed } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { ELocale } from '~~/types/enums'
import { ResponseCode } from '~/utils/constant'
import { getTodayDate, getXOssWebpSize, handleNewsUrl } from '~/utils/common'
import xss from 'xss'
import _ from 'lodash'

export const useWikiDetail = async (
  id: string,
  historyId: string,
  historyStatus: string,
  exhibitionAliasId: string,
  locale: ELocale
) => {
  useLang(locale)
  const route = useRoute()
  const { t } = useI18n()

  // Reactive data
  const fair = ref<any>({})
  const fairImage = ref<any[]>([])
  const historyFairList = ref<any[]>([])
  const hotWikiList = ref<any[]>([])
  const fairNews = ref<any[]>([])
  const innerChainActive = ref(0)
  const innerChainAreaList = ref<any[]>([])
  const innerChainIndustryList = ref<any[]>([])
  const innerChainExhList = ref<any[]>([])

  // Computed
  const title = ref('')
  const langApi = ref(locale === ELocale.en ? 'en' : 'cn')
  const updateTime = ref(getTodayDate())

  // Methods
  const fetchData = async () => {
    try {
      const { useHttp } = await import('~/composables/useHttp')

      // 参数错误 显示404(正确参数: wiki/3357-396-0-0.html, 错误:wiki/3357-0-0-396.html)
      if (historyId === '0') {
        throw createError({
          statusCode: 404,
          statusMessage: 'Page not found'
        })
      }

      const res: any[] = await Promise.all([
        historyStatus === '1'
          ? useHttp.post(`/api/wiki/history/detail`, {
              id: historyId,
              exhibitionId: id,
              aliasId: exhibitionAliasId
            })
          : useHttp.post(`/api/wiki/detail`, {
              id: historyId,
              exhibitionId: id
            }),
        useHttp.post('/api/wiki/exhibition/hotList', {
          locale: langApi.value,
          sortHot: 'desc',
          pageNum: 1,
          pageSize: 10
        }),
        useHttp.post(`/api/newsApi/getNewsbyExhibitionId/`, {
          locale: langApi.value,
          exhibitionId: id,
          pageNum: 1,
          pageSize: 13
        })
      ])

      const [fairRes, hotFairRes, newsByFairIdRes] = res

      // 展会资讯
      if (historyStatus === '0') {
        if (newsByFairIdRes.code === ResponseCode.SUCCESS) {
          fairNews.value = newsByFairIdRes.data.records.map((n: any) => ({
            newsId: n.id,
            viewUrl: n.viewUrl,
            newsName: n.title,
            views: n.viewCount,
            logo: n.coverImg,
            publishTime: n.publishTime,
            description: n.description
          }))
        }
      }

      // 热门百科
      if (hotFairRes.code === ResponseCode.SUCCESS) {
        let list = hotFairRes.data.records

        if (list.length > 0) {
          let idList: any = []
          list.map((i: any) => idList.push(i.exhibitionId))

          const aliasRes = await useHttp.post(`/api/wiki/aliasList`, {
            exhibitionIdList: idList
          })

          if (aliasRes.code === ResponseCode.SUCCESS) {
            let aliasList: any = aliasRes.data
            let resList: any = []
            list.map((item: any) => {
              let findRes: any = aliasList.find(
                (x: any) => x.exhibitionId == item.exhibitionId && x.isPrimary == 1
              )
              if (!findRes) {
                findRes = aliasList.find((x: any) => x.exhibitionId == item.id)
              }
              if (findRes) {
                resList.push({
                  id: findRes ? findRes.id : '',
                  exhibitionId: item.exhibitionId,
                  exhibitionName: item.exhibitionName,
                  exhibitionAliasName: findRes ? findRes.exhibitionAlias : item.exhibitionName,
                  cityName: item.cityName,
                  lunchTime: item.lunchTime
                })
              }
            })
            hotWikiList.value = resList
          }
        }
      }

      // 展会详情
      if (fairRes.code === ResponseCode.SUCCESS) {
        fair.value = fairRes.data || {}
        title.value = `${fair.value.exhibitionAlias || ''} - 展会百科 - 聚展`
        if (
          !fair.value.hasOwnProperty('exhibitionAlias') ||
          (fair.value.exhibitionAlias ?? '') === ''
        ) {
          fair.value.exhibitionAlias = ''
        }

        if (fair.value.photos) {
          fair.value.photos.slice(0, 6).map((item: { resourceUrl: any }) => {
            fairImage.value.push(item.resourceUrl)
          })
        }

        if (historyStatus === '1') {
          let list: any = fairRes.data.relateds || []
          let imageList: any = []
          list.map((item: any) => {
            item['newsId'] = item.relateId
            item['newsName'] = item.title
            item['logo'] = item.picture
            item['description'] = item.profile
            if (item.type == 4) {
              imageList.push(item.url)
            }
          })
          // 历届资讯
          fairNews.value = list.filter((i: any) => i.type == 1)
          // 历届现场图片
          fairImage.value = imageList.slice(0, 6)
        }

        const industryId = [
          fair.value.industry1,
          fair.value.industry2,
          fair.value.industry3
        ].filter((id: any) => !!id)

        // 获取历届列表
        if ((fair.value.exhibitionAlias ?? '') !== '') {
          const historyRes = await useHttp.post('/api/wiki/history/list', {
            locale: langApi.value,
            exhibitionAlias: fair.value.exhibitionAlias,
            exhibitionId: fair.value.exhibitionId,
            startSessionNum: fair.value.numSessions,
            pageNum: 1,
            pageSize: 10
          })

          if (historyRes.code === ResponseCode.SUCCESS) {
            let list: any = historyRes.data.records
            list.map((item: any) => {
              let numSessions = item.numSessions ? '第' + item.numSessions + '届' : ''
              item['name'] = `${numSessions}${item.exhibitionName}`
            })
            historyFairList.value = list
          }
        }

        // 检查展会状态
        if (
          (!_.isNil(fair.value.online) && fair.value.online !== 1 && id !== '4') ||
          _.isNil(fairRes.data)
        ) {
          throw createError({
            statusCode: 404,
            statusMessage: 'Page not found'
          })
        }

        // 内链
        let paramsIndustry: any[] = [...industryId]
        const innerChainAllRes: any = await Promise.all([
          useHttp.post('/api/innerChain/page', {
            category: '热门地区',
            pageSize: 16
          }),
          useHttp.post('/api/innerChain/page', {
            category: '热门行业',
            industryIdList: paramsIndustry,
            pageSize: 16
          }),
          useHttp.post('/api/innerChain/page', {
            category: '热门展会',
            industryIdList: paramsIndustry,
            pageSize: 16
          })
        ])

        const [innerChainAreaRes, innerChainIndustryRes, innerChainExhRes] = innerChainAllRes

        if (innerChainAreaRes.code === ResponseCode.SUCCESS) {
          innerChainAreaList.value = innerChainAreaRes.data.records
        }

        if (innerChainIndustryRes.code === ResponseCode.SUCCESS) {
          innerChainIndustryList.value = innerChainIndustryRes.data.records
          let len = innerChainIndustryList.value.length
          if (len < 16) {
            const innerChainArea2Res = await useHttp.post('/api/innerChain/page', {
              category: '热门行业',
              pageSize: 16 - len
            })

            if (innerChainArea2Res.code === ResponseCode.SUCCESS) {
              innerChainIndustryList.value = innerChainIndustryList.value.concat(
                innerChainArea2Res.data.records
              )
            }
          }
        }

        if (innerChainExhRes.code === ResponseCode.SUCCESS) {
          innerChainExhList.value = innerChainExhRes.data.records
          let len = innerChainExhList.value.length
          if (len < 16) {
            const innerChainExh2Res = await useHttp.post('/api/innerChain/page', {
              category: '热门展会',
              pageSize: 16 - len
            })

            if (innerChainExh2Res.code === ResponseCode.SUCCESS) {
              innerChainExhList.value = innerChainExhList.value.concat(
                innerChainExh2Res.data.records
              )
            }
          }
        }
      }
    } catch (error) {
      console.error('Error fetching wiki detail data:', error)
      throw error
    }
  }

  const handleHotTab = (index: number) => {
    innerChainActive.value = index
  }

  const goToInformation = () => {
    if (process.client) {
      window.open(`/expoinfo/${id}.html`)
    }
  }

  const getContent = (content: string, exhibitionId: string) => {
    if (!content) return ''
    return content.replace(/<img[^>]*>/g, match => {
      return match.replace(/src="([^"]*)"/g, `src="$1" data-exhibition-id="${exhibitionId}"`)
    })
  }

  const getXOssProcess = (options: { w: number; h: number }) => {
    return `?x-oss-process=image/resize,m_fill,w_${options.w},h_${options.h}/quality,q_90`
  }

  const formatLogo = (value: string) => {
    let parse: any = value
    if (typeof value === 'string') {
      try {
        parse = JSON.parse(value)[0]
          .replace('http://', 'https://')
          .replace('http://w3-file.jufair.com', 'https://w3-file.jufair.com')
      } catch (error) {
        parse = value
        console.error(error)
      }
    }
    return parse + '!logo'
  }

  const formatIndustry = (data: string) => {
    try {
      return data.split(',')[0] + '展会'
    } catch (error) {
      return data + '展会'
    }
  }

  return {
    fair,
    fairImage,
    historyFairList,
    hotWikiList,
    fairNews,
    innerChainActive,
    innerChainAreaList,
    innerChainIndustryList,
    innerChainExhList,
    title,
    langApi,
    updateTime,
    fetchData,
    handleHotTab,
    goToInformation,
    getContent,
    getXOssWebpSize,
    formatLogo,
    formatIndustry,
    xss,
    handleNewsUrl,
    $t: t
  }
}
